import React, { useState } from 'react';
import { AlertTriangle, X, ExternalLink, GitMerge } from 'lucide-react';
import { Badge } from './ui/badge';
import { MergeDuplicatesDialog } from './MergeDuplicatesDialog';

const SOURCE_LABELS = {
  customer: 'Customers',
  meta_lead: 'Meta Leads',
  wix_lead: 'Website Leads',
  email_lead: 'Email Leads',
};

const SOURCE_COLORS = {
  customer: 'text-blue-700',
  meta_lead: 'text-indigo-700',
  wix_lead: 'text-teal-700',
  email_lead: 'text-amber-700',
};

const MATCH_LABELS = {
  email: 'Email',
  name: 'Name',
  phone: 'Phone',
};

export const DuplicateBadge = ({ matches, currentSource, currentId, compact = false, onMerged }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);

  if (!matches || matches.length === 0) return null;

  const customerMatches = matches.filter(m => m.source === 'customer');
  const hasCustomerMatch = customerMatches.length > 0;

  const getLink = (match) => {
    if (match.source === 'customer') return `/customers/${match.id}`;
    if (match.source === 'meta_lead') return '/meta-leads';
    if (match.source === 'wix_lead') return '/website-leads';
    if (match.source === 'email_lead') return '/email-leads';
    return null;
  };

  const handleMergeClick = (match) => {
    setShowPopover(false);
    setMergeTarget(match);
  };

  const handleMerged = () => {
    setMergeTarget(null);
    onMerged?.();
  };

  return (
    <>
      <div className="relative inline-block">
        <button
          onClick={(e) => { e.stopPropagation(); setShowPopover(!showPopover); }}
          data-testid="duplicate-badge"
        >
          <Badge
            className={`cursor-pointer text-[11px] font-medium gap-1 ${
              hasCustomerMatch
                ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200'
                : 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200'
            }`}
          >
            <AlertTriangle className="w-3 h-3" />
            {compact ? matches.length : `${matches.length} Duplicate${matches.length > 1 ? 's' : ''}`}
          </Badge>
        </button>

        {showPopover && (
          <>
            <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setShowPopover(false); }} />
            <div
              className="absolute z-50 mt-1 left-0 w-80 bg-white border border-slate-200 rounded-lg shadow-lg p-3 space-y-2"
              onClick={(e) => e.stopPropagation()}
              data-testid="duplicate-popover"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-700">Possible Duplicates</p>
                <button onClick={() => setShowPopover(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {matches.map((match, i) => {
                  const link = getLink(match);
                  const canMerge = currentSource && currentId;
                  return (
                    <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50 text-xs">
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${SOURCE_COLORS[match.source] || 'text-slate-700'}`}>
                          {match.name}
                        </p>
                        <div className="flex gap-1.5 mt-0.5 flex-wrap">
                          <span className={`font-semibold ${SOURCE_COLORS[match.source] || 'text-slate-500'}`}>
                            in {SOURCE_LABELS[match.source]}
                          </span>
                          <span className="text-slate-300">|</span>
                          <span className="text-slate-500">Matched by {MATCH_LABELS[match.match_type]}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        {canMerge && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMergeClick(match); }}
                            className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                            title="Merge records"
                            data-testid={`merge-btn-${i}`}
                          >
                            <GitMerge className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {link && (
                          <a
                            href={link}
                            className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`duplicate-link-${i}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {mergeTarget && currentSource && currentId && (
        <MergeDuplicatesDialog
          open={!!mergeTarget}
          onClose={() => setMergeTarget(null)}
          sourceType={currentSource}
          sourceId={currentId}
          targetType={mergeTarget.source}
          targetId={mergeTarget.id}
          onMerged={handleMerged}
        />
      )}
    </>
  );
};
